import { config } from '../../config';
import Feed from '../components/Feed/Feed';
import type { IFeedItem } from '../components/FeedItem/FeedItem';
import { FormattedText } from '../components/FormattedText';
import NotFound from '../components/NotFound';
import PageMeta from '../components/PageMeta';
import SimpleMap from '../components/SimpleMap';
import { getNews } from '../features/queries/getNews';
import { getNewsDetail } from '../features/queries/getNewsDetail';
import { entityId } from '../lib/ids';
import { useQ } from '../lib/query';
import { __, formatDate, hasLocation } from '../utils';
import { ContentCopy, Check } from '@mui/icons-material';
import {
  Box,
  Card,
  CardMedia,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { encode } from 'pluscodes';
import React from 'react';
import { useParams } from 'react-router';

const NewsDetailPage: React.FC = () => {
  const { id: idParam } = useParams() as { id: string };
  const id = entityId(idParam, 'news');
  const [copiedPlusCode, setCopiedPlusCode] = React.useState(false);

  const {
    data: newsDetail,
    isLoading: isLoadingDetail,
    error,
  } = useQ(getNewsDetail, {
    id,
  });

  const { data: similarData = [], isLoading: isLoadingSimilar } = useQ(
    getNews,
    {
      category: newsDetail?.categories?.map((i) => i.id) || [],
      limit: 5,
    },
    {
      enabled: !isLoadingDetail && !!newsDetail?.categories?.length,
      select: (data) =>
        data.map(
          (item) =>
            ({
              ...item,
              type: 'news',
              id: item.id,
              title: item.title,
              description: item.description,
              image: item.image,
              location: item.location as {
                type: 'Point';
                coordinates: [number, number];
              },
            }) as IFeedItem,
        ),
    },
  );

  const plusCode = React.useMemo(() => {
    if (!isLoadingDetail && hasLocation(newsDetail)) {
      return encode({
        latitude: newsDetail.location.coordinates[1],
        longitude: newsDetail.location.coordinates[0],
      });
    }
    return '';
  }, [isLoadingDetail, newsDetail]);

  const handleCopyPlusCode = () => {
    if (navigator.clipboard) {
      try {
        navigator.clipboard.writeText(plusCode);
        setCopiedPlusCode(true);
        setTimeout(() => setCopiedPlusCode(false), 2000);
      } catch (err) {
        console.error('Clipboard API error:', err);
      }
    } else {
      // fallback pre HTTP alebo staré prehliadače
      const textarea = document.createElement('textarea');
      textarea.value = plusCode;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopiedPlusCode(true);
        setTimeout(() => setCopiedPlusCode(false), 2000);
      } catch (err) {
        console.error('Fallback copy error:', err);
      }
      document.body.removeChild(textarea);
    }
  };

  if (error) {
    return (
      <NotFound
        message={__('Novinka sa nenašla')}
        backText='Späť na novinky'
        backUrl='/'
      />
    );
  }

  return (
    <Stack
      direction={{ lg: 'row' }}
      gap={2}
      sx={{ overflowX: 'hidden', width: '100%' }}
    >
      {newsDetail ? (
        <PageMeta
          title={`${__('Novinka')}: ${newsDetail.title}`}
          description={newsDetail.description}
          image={newsDetail.image}
          type='article'
        />
      ) : null}
      <Box flex={2}>
        <Card
          sx={{
            gap: 0,
            p: 0,
          }}
        >
          {isLoadingDetail ? (
            <>
              <Skeleton
                variant='rectangular'
                width={'100%'}
                sx={{
                  paddingTop: '56.25%',
                }}
              />

              <Skeleton variant='rectangular' width={200} height={20} />
              <Skeleton variant='rectangular' width={320} height={36} />
              <Skeleton variant='rectangular' width={280} height={24} />
            </>
          ) : (
            <>
              {newsDetail.image && (
                <CardMedia
                  image={newsDetail.image}
                  sx={{
                    aspectRatio: '16 / 9',
                    objectFit: 'contain',
                  }}
                />
              )}
              <Stack p={2} gap={1}>
                <Stack direction={'row'} gap={3}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {formatDate(new Date(newsDetail.date))}
                  </Typography>

                  <Stack direction={'row'} gap={1}>
                    {newsDetail.categories.map((category, index) => (
                      <React.Fragment key={category.id}>
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                          }}
                        >
                          {category.name}
                        </Typography>

                        {index < newsDetail.categories.length - 1 && (
                          <Typography
                            variant='caption'
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            •
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Stack>
                </Stack>

                <Typography variant='h3' sx={{ wordBreak: 'break-word' }}>
                  {__(newsDetail.title)}
                </Typography>

                <FormattedText text={newsDetail.description} />
              </Stack>
            </>
          )}
        </Card>
      </Box>

      <Stack
        sx={{
          flex: 1,
          gap: 1,
          width: {
            lg: 400,
          },
          minWidth: {
            lg: 400,
          },
        }}
      >
        <Card
          sx={{
            gap: 0,
            p: 0,
          }}
        >
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            p={2}
          >
            <Typography variant='h6'>{__('Lokalita')}</Typography>
            {plusCode && (
              <Stack direction='row' alignItems='center' gap={1}>
                <Typography
                  variant='body2'
                  component={'a'}
                  href={`${config.PLUS_CODES_BASE_URL}/${encodeURIComponent(plusCode)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {plusCode}
                </Typography>
                <Box
                  alignItems={'center'}
                  justifyContent={'center'}
                  onClick={handleCopyPlusCode}
                  title='Skopírovať Plus Code'
                  sx={{ height: 16, width: 16, cursor: 'pointer' }}
                >
                  {copiedPlusCode ? (
                    <Check
                      fontSize='small'
                      color='success'
                      sx={{ fontSize: 18 }}
                    />
                  ) : (
                    <ContentCopy fontSize='small' sx={{ fontSize: 18 }} />
                  )}
                </Box>
              </Stack>
            )}
          </Stack>

          <Divider />

          <Box p={2}>
            {isLoadingDetail ? (
              <Skeleton variant='rectangular' width={'100%'} height={320} />
            ) : hasLocation(newsDetail) ? (
              <Box
                sx={{
                  width: '100%',
                  height: 320,
                }}
              >
                <SimpleMap
                  location={[
                    newsDetail.location.coordinates[0],
                    newsDetail.location.coordinates[1],
                  ]}
                />
              </Box>
            ) : (
              <Typography variant='caption' fontStyle={'italic'}>
                {__('Táto novinka nemá priradenú žiadnu lokalitu')}
              </Typography>
            )}
          </Box>
        </Card>

        <Card
          sx={{
            p: 0,
            gap: 0,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              p: 2,
            }}
          >
            {__('Podobné novinky')}
          </Typography>
          <Divider />

          <Feed
            items={similarData}
            isLoading={isLoadingDetail || isLoadingSimilar}
            hideImage
          />
        </Card>
      </Stack>
    </Stack>
  );
};

export default NewsDetailPage;
